import { Column, PromptCard, Row, type Prompt } from "@1js/pl-card-components";
import { type Group } from "@1js/pl-types";
import type { ToastIntent } from "@fluentui/react-components";
import * as React from "react";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import type { HandlePromptsTabSelected } from "../AllPrompts/AllPrompts.web.data";
import { useStyles } from "../AllPrompts/AllPrompts.web.styles";
import { useColumnStyle } from "../AllPrompts/useColumnStyle.hook";
import { useYourPrompts } from "./YourPrompts.hook.web";

export type PromptCardPanelProps = {
  prompts: Prompt[];
  resultsAnnouncementId: string;
  promptsToDisplay: Prompt[];
  setLastFocusedPromptId: (promptId: string) => void;
  setCurrentFocusedPromptId: (promptId: string) => void;
  promptRefs: React.MutableRefObject<
    Map<string, React.RefObject<HTMLDivElement>>
  >;
  currentFocusedPromptId: string | null;
  isWorkgroupPublishingEnabled: boolean;
  shareToWorkgroup: (prompts: Prompt[]) => void;
  handleShareViaCopyLinkClicked?: (prompt: Prompt) => Promise<void>;
  handleShareViaEmailClicked?: (prompt: Prompt) => Promise<void>;
  handleCardClick: (prompt: Prompt) => void;
  onPromptBookmarkToggled: (
    prompt: Prompt,
    value?: boolean,
    toastNotify?: (
      intent: ToastIntent,
      title: string,
      body?: string,
      footer?: JSX.Element,
      headerButton?: JSX.Element
    ) => void,
    nextPromptCardToFocusOn?: React.RefObject<HTMLDivElement>
  ) => Promise<void>;
  onDelete?: (prompt: Prompt) => void;
  groups?: Group[];
  handlePromptsTabSelected: HandlePromptsTabSelected;
  onBookmarkToggled: (() => void) | undefined;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PromptCardPanel: React.FC<PromptCardPanelProps> = ({
  prompts,
  resultsAnnouncementId,
  promptsToDisplay,
  setLastFocusedPromptId,
  setCurrentFocusedPromptId,
  promptRefs,
  currentFocusedPromptId,
  isWorkgroupPublishingEnabled,
  shareToWorkgroup,
  handleShareViaCopyLinkClicked,
  handleShareViaEmailClicked,
  handleCardClick,
  onPromptBookmarkToggled,
  onDelete,
  groups,
  handlePromptsTabSelected,
  onBookmarkToggled,
  setDialogOpen,
}) => {
  const styles = useStyles();

  const { isFlightActive, chooseBestProductForHandoff } = useYourPrompts({
    handlePromptsTabSelected,
    onBookmarkToggled,
    onDelete,
    setDialogOpen,
  });

  const appearance = isFlightActive("isMosAppPromptCardV2Enabled")
    ? "web-v3"
    : "web";

  const cardColStyles = useColumnStyle();

  const { state } = React.useContext(LibraryContext);

  const firstPromptId = promptsToDisplay[0].Id;

  //Used when redirected to Your Prompts tab after liking a prompt
  React.useEffect(() => {
    if (state.shouldFocusFirstLikedPrompt && promptRefs.current.size > 0) {
      const firstPrompt = promptRefs.current.get(firstPromptId);
      if (firstPrompt && firstPrompt.current) {
        firstPrompt.current.focus();
      }
    }
  }, [state.shouldFocusFirstLikedPrompt, firstPromptId, promptRefs]);

  return (
    <Row
      role="list"
      aria-label="Prompt"
      aria-describedby={resultsAnnouncementId}
      className={styles.cards}
      data-testid="prompt-cards"
    >
      {promptsToDisplay.map((prompt: Prompt, idx: number) => {
        if (!promptRefs.current.has(prompt.Id)) {
          promptRefs.current.set(prompt.Id, React.createRef());
        }
        const handleFocus = () => {
          setLastFocusedPromptId(prompt.Id);
          setCurrentFocusedPromptId(prompt.Id);
        };

        const handleFavoriteToggled = (prompt: Prompt, value: boolean) => {
          let nextRef: React.RefObject<HTMLDivElement> | undefined = undefined;
          // If there is a next prompt, get its ref
          if (idx < promptsToDisplay.length - 1) {
            nextRef = promptRefs.current.get(promptsToDisplay[idx + 1].Id);
          } else if (idx > 0) {
            // Otherwise, use the previous prompt if available
            nextRef = promptRefs.current.get(promptsToDisplay[idx - 1].Id);
          }
          onPromptBookmarkToggled(prompt, value, undefined, nextRef);
        };
        return (
          <Column key={prompt.Id} size={3} className={cardColStyles}>
            <PromptCard
              appearance={appearance}
              key={prompt.Id}
              displayPosition={idx}
              isPromptFocused={currentFocusedPromptId === prompt.Id}
              onFocus={handleFocus}
              prompt={prompt}
              isFavoriteEnabled={isFlightActive("isBookmarksEnabled")}
              isAgentPromptFavoriteEnabled={isFlightActive(
                "isAgentPromptFavoriteEnabled"
              )}
              isPromptSavingEnabled={isFlightActive("isPromptSavingEnabled")}
              isPromptSharingEnabled={isFlightActive("isPromptSharingEnabled")}
              isPromptSharingViaEmailEnabled={isFlightActive(
                "isPromptSharingViaEmailEnabled"
              )}
              isPromptSharingToWorkgroupEnabled={
                isWorkgroupPublishingEnabled &&
                (isFlightActive(
                  "isWorkgroupPublishingMicrosoftPromptsEnabled"
                ) ||
                  prompt.Origin === "User")
              }
              onShareToWorkgroupClicked={shareToWorkgroup}
              onShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
              onShareViaEmailClicked={handleShareViaEmailClicked}
              onClicked={handleCardClick}
              onFavoriteToggled={handleFavoriteToggled}
              isBadgesEnabled={true}
              isPromptLikingEnabled={isFlightActive("isPromptLikingEnabled")}
              ref={promptRefs.current.get(prompt.Id)}
              onDelete={onDelete}
              aria-posinset={idx + 1}
              aria-setsize={prompts.length}
              groups={groups}
              handoffProduct={chooseBestProductForHandoff(prompt)}
              extensionIconMap={
                isFlightActive("isAgentPromptSavingEnabled") ||
                isFlightActive("isAgentPromptFavoriteEnabled")
                  ? state.extensionIconMap
                  : undefined
              }
            />
          </Column>
        );
      })}
    </Row>
  );
};
